import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../components/Layout';
import api from '../utils/api';

export default function InvoicesPage() {
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [markPaidModal, setMarkPaidModal] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = () => {
    if (!activeCompany) return;
    setLoading(true);
    api.get(`/invoices?company_id=${activeCompany.id}`)
      .then(r => setInvoices(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [activeCompany]);

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      showToast('Invoice deleted');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Cannot delete', 'error');
    }
  };

  const handleSend = async (inv) => {
    if (!inv.client_email && !window.confirm('No email on file for this client. Are you sure?')) return;
    setActionLoading(`send-${inv.id}`);
    try {
      await api.post(`/invoices/${inv.id}/send`);
      showToast(`Invoice sent to client!`);
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Send failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

const handleDownload = async (inv) => {
  setActionLoading(`pdf-${inv.id}`);
  try {
    const token = localStorage.getItem('token');
    
    // If PDF already exists, download directly without regenerating
    if (inv.pdf_path) {
      const link = document.createElement('a');
      link.href = `/api/invoices/${inv.id}/download?token=${token}`;
      link.setAttribute('download', `Invoice-${inv.invoice_number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('PDF downloaded!');
    } else {
      // Generate PDF first then download
      await api.post(`/invoices/${inv.id}/generate-pdf`);
      const link = document.createElement('a');
      link.href = `/api/invoices/${inv.id}/download?token=${token}`;
      link.setAttribute('download', `Invoice-${inv.invoice_number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('PDF downloaded!');
      // Update local state so next click skips generation
      setInvoices(prev => prev.map(i => 
        i.id === inv.id ? { ...i, pdf_path: true } : i
      ));
    }
  } catch (e) {
    showToast(e.response?.data?.error || 'PDF generation failed', 'error');
  } finally {
    setActionLoading(null);
  }
};

  const handleMarkPaid = async () => {
    if (!markPaidModal) return;
    setActionLoading(`paid-${markPaidModal.id}`);
    try {
      const res = await api.post(`/invoices/${markPaidModal.id}/mark-paid`, { payment_date: paymentDate });
      showToast(res.data.message);
      setMarkPaidModal(null);
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Error', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = inv.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
      inv.client_name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const symbol = (inv) => inv.currency === 'INR' ? '₹' : '$';

  const statusBadge = (s) => <span className={`badge badge-${s}`}>{s}</span>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Invoices</h2>
          <p>{activeCompany?.name} · {filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/invoices/new')}>+ New Invoice</button>
      </div>

      <div className="filter-bar">
        <input className="search-input" placeholder="Search invoices or clients..." value={search} onChange={e => setSearch(e.target.value)} />
        {['all', 'draft', 'sent', 'paid'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`btn btn-sm ${filterStatus === s ? 'btn-secondary' : 'btn-outline'}`}
            style={{ textTransform: 'capitalize' }}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div className="loading">Loading...</div> : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="icon">🧾</div>
            <p>{search || filterStatus !== 'all' ? 'No invoices match your filter' : 'No invoices yet.'}</p>
            {!search && filterStatus === 'all' && <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate('/invoices/new')}>+ New Invoice</button>}
          </div>
        ) : (
          <table className="data-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Date</th>
                <th>Due</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th>Curr.</th>
                <th>Status</th>
                <th>GST</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{inv.invoice_number}</td>
                  <td style={{ fontWeight: 500 }}>{inv.client_name}</td>
                  <td style={{ color: '#888', fontSize: 13 }}>{inv.issue_date}</td>
                  <td style={{ color: inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== 'paid' ? '#dc2626' : '#888', fontSize: 13 }}>
                    {inv.due_date || '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{symbol(inv)}{parseFloat(inv.total).toFixed(2)}</td>
                  <td style={{ textAlign: 'right', color: parseFloat(inv.balance_due) > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                    {symbol(inv)}{parseFloat(inv.balance_due).toFixed(2)}
                  </td>
                  <td><span className={`badge badge-${inv.currency.toLowerCase()}`}>{inv.currency}</span></td>
                  <td>{statusBadge(inv.status)}</td>
                  <td style={{ fontSize: 13 }}>
                    {inv.currency === 'INR' ? (inv.gst_sent ? <span style={{ color: '#16a34a' }}>✓ Sent</span> : <span style={{ color: '#aaa' }}>Pending</span>) : <span style={{ color: '#ddd' }}>N/A</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => navigate(`/invoices/${inv.id}/edit`)} title="Edit">✏️</button>
                      <button className="btn btn-outline btn-sm" onClick={() => handleDownload(inv)}
                        disabled={actionLoading === `pdf-${inv.id}`} title="Download PDF">
                        {actionLoading === `pdf-${inv.id}` ? '...' : '⬇️'}
                      </button>
                      {inv.status !== 'paid' && (
                        <button className="btn btn-outline btn-sm" onClick={() => handleSend(inv)}
                          disabled={actionLoading === `send-${inv.id}`} title="Send to client">
                          {actionLoading === `send-${inv.id}` ? '...' : '📧'}
                        </button>
                      )}
                      {inv.status !== 'paid' && (
                        <button className="btn btn-success btn-sm" onClick={() => { setMarkPaidModal(inv); setPaymentDate(new Date().toISOString().split('T')[0]); }} title="Mark as paid">
                          ✓ Paid
                        </button>
                      )}
                      {inv.status !== 'paid' && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(inv)} title="Delete">🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mark Paid Modal */}
      {markPaidModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMarkPaidModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Mark Invoice as Paid</h3>
              <button className="modal-close" onClick={() => setMarkPaidModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: '#555' }}>
                Invoice <strong>{markPaidModal.invoice_number}</strong> — {markPaidModal.currency === 'INR' ? '₹' : '$'}{parseFloat(markPaidModal.total).toFixed(2)}
              </p>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Payment Date</label>
                <input className="form-control" type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
              </div>
              {markPaidModal.currency === 'INR' && (
                <div style={{ background: '#fef9c3', padding: '10px 14px', borderRadius: 7, fontSize: 13, color: '#854d0e' }}>
                  📋 A GST copy will automatically be sent to your GST department email upon marking paid.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setMarkPaidModal(null)}>Cancel</button>
              <button className="btn btn-success" onClick={handleMarkPaid} disabled={!!actionLoading}>
                {actionLoading ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
